import { apiService } from './api';

/**
 * Broadcasts service for handling broadcast-related API calls
 */
export const broadcastsService = {
  /**
   * Get all broadcasts for a project
   * @param {string} projectId - Project ID
   * @param {Object} params - Query parameters (page, limit, status, search)
   * @returns {Promise<Object>} Broadcasts response
   */
  getBroadcasts: async (projectId, params = {}) => {
    const queryParams = new URLSearchParams({
      projectId,
      ...params,
    }).toString();
    return await apiService.get(`/broadcasts?${queryParams}`);
  },

  /**
   * Get a specific broadcast by ID
   * @param {string} broadcastId - Broadcast ID
   * @returns {Promise<Object>} Broadcast response
   */
  getBroadcast: async broadcastId => {
    return await apiService.get(`/broadcasts/${broadcastId}`);
  },

  /**
   * Create a new broadcast
   * @param {FormData|Object} broadcastData - Broadcast data (FormData for file uploads, Object for JSON)
   * @returns {Promise<Object>} Created broadcast response
   */
  createBroadcast: async broadcastData => {
    return await apiService.post('/broadcasts', broadcastData);
  },

  /**
   * Delete a broadcast (only for scheduled broadcasts)
   * @param {string} broadcastId - Broadcast ID
   * @returns {Promise<Object>} Delete response
   */
  deleteBroadcast: async broadcastId => {
    return await apiService.delete(`/broadcasts/${broadcastId}`);
  },

  /**
   * Pause a broadcast
   * @param {string} broadcastId - Broadcast ID
   * @returns {Promise<Object>} Pause response
   */
  pauseBroadcast: async broadcastId => {
    return await apiService.post(`/broadcasts/${broadcastId}/pause`);
  },

  /**
   * Resume a paused broadcast
   * @param {string} broadcastId - Broadcast ID
   * @returns {Promise<Object>} Resume response
   */
  resumeBroadcast: async broadcastId => {
    return await apiService.post(`/broadcasts/${broadcastId}/resume`);
  },

  /**
   * Get broadcast recipients
   * @param {string} broadcastId - Broadcast ID
   * @param {Object} params - Query parameters (page, limit, status, pricingType)
   * @returns {Promise<Object>} Recipients response
   */
  getBroadcastRecipients: async (broadcastId, params = {}) => {
    // Filter out undefined/null values so URLSearchParams doesn't stringify them
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null && v !== ''
      )
    );
    const queryParams = new URLSearchParams(cleanParams).toString();
    return await apiService.get(
      `/broadcasts/${broadcastId}/recipients?${queryParams}`
    );
  },

  /**
   * Export broadcast recipients with optional filters
   * @param {string} broadcastId - Broadcast ID
   * @param {Object} params - { format, status, pricingType }
   * @returns {Promise<Blob>} File blob
   */
  exportBroadcastRecipients: async (broadcastId, params = {}) => {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null && v !== ''
      )
    );
    const queryParams = new URLSearchParams(cleanParams).toString();
    return await apiService.get(
      `/broadcasts/${broadcastId}/export?${queryParams}`,
      { responseType: 'blob' }
    );
  },

  /**
   * Get broadcast analytics
   * @param {string} broadcastId - Broadcast ID
   * @returns {Promise<Object>} Analytics response
   */
  getBroadcastAnalytics: async broadcastId => {
    return await apiService.get(`/broadcasts/${broadcastId}/analytics`);
  },

  /**
   * Export broadcast report
   * @param {string} broadcastId - Broadcast ID
   * @param {string} format - Export format (csv, excel, pdf)
   * @returns {Promise<Blob>} File blob
   */
  exportBroadcast: async (broadcastId, format = 'csv') => {
    return await apiService.get(
      `/broadcasts/${broadcastId}/export?format=${format}`,
      {
        responseType: 'blob',
      }
    );
  },

  /**
   * Download contact template for upload
   * @param {string} templateId - Template ID
   * @returns {Promise<Blob>} Excel template file
   */
  downloadContactTemplate: async templateId => {
    return await apiService.get(
      `/broadcasts/contact-template?templateId=${templateId}`,
      {
        responseType: 'blob',
      }
    );
  },

  /**
   * Get contact count with filters
   * @param {string} projectId - Project ID
   * @param {Object} filters - Filter criteria
   * @returns {Promise<Object>} Count response
   */
  getContactCount: async (projectId, filters = {}) => {
    return await apiService.post('/contacts/count', {
      projectId,
      ...filters,
    });
  },
};

export default broadcastsService;
